import { Injectable } from "@angular/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { Network } from "@capacitor/network";
import { BehaviorSubject } from "rxjs";
import { MobileService } from "../mobile/ios.service";
import { Song } from "src/app/models/songModel";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { DisciplineModel } from "src/app/models/disciplineModel";

@Injectable({
  providedIn: 'root'
})
export class OfflineService {
  public isOffline$ = new BehaviorSubject<boolean>(false);

  constructor(private mobileService: MobileService) {
    Network.getStatus().then(s => this.isOffline$.next(!s.connected));
    Network.addListener('networkStatusChange', s => this.isOffline$.next(!s.connected));
  }

  async saveSong(blob: Blob, song: Song, coverBlob?: Blob): Promise<string> {
    try {
      const base64Data = await this.convertBlobToBase64(blob);

      if (this.mobileService.isApp) {
        const result = await Filesystem.writeFile({
          path: `${song.id}.mp3`,
          data: base64Data,
          directory: Directory.Data,
          recursive: true
        });
        const songToCache = await this.buildSongWithBase64Cover(song, coverBlob);
        await Filesystem.writeFile({
          path: `${song.id}.json`,
          data: JSON.stringify(songToCache),
          directory: Directory.Data,
          encoding: Encoding.UTF8
        });
        return result.uri;
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${song.id}.mp3`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        return 'Downloaded to browser';
      }
    } catch (error) {
      console.error('Errore nel salvataggio su disco:', error);
      throw error;
    }
  }

  // Sostituisce song.cover con base64 in modo che le <img> funzionino offline
  // senza dipendere da URL nativi o Filesystem.getUri.
  // Usa CapacitorHttp (nativo) come fallback per bypassare le restrizioni CORS
  // della WebView (Origin: http://localhost non è whitelistato dal backend).
  private async buildSongWithBase64Cover(song: Song, coverBlob?: Blob): Promise<Song> {
    if (!song.cover) return song;
    try {
      let base64Cover: string | null = null;
      if (coverBlob) {
        base64Cover = await this.convertBlobToBase64(coverBlob);
      } else {
        base64Cover = await this.downloadCoverNative(song.cover);
      }
      if (!base64Cover) return song;
      return { ...song, cover: base64Cover };
    } catch {
      return song;
    }
  }

  private async downloadCoverNative(url: string): Promise<string | null> {
    try {
      let token: string | null = null;
      try { token = JSON.parse(localStorage.getItem('token')); } catch { token = localStorage.getItem('token'); }
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await CapacitorHttp.get({ url, headers, responseType: 'arraybuffer' });
      if (response.status < 200 || response.status >= 300 || !response.data) return null;

      const rawCt = response.headers?.['content-type'] ?? response.headers?.['Content-Type'] ?? 'image/jpeg';
      const contentType = rawCt.split(';')[0].trim();
      const data = response.data;

      if (typeof data === 'string') {
        // Native (Android/iOS): arraybuffer viene restituito come stringa base64
        return `data:${contentType};base64,${data}`;
      }
      if (data instanceof ArrayBuffer) {
        // Web: ArrayBuffer reale
        const bytes = new Uint8Array(data);
        let binary = '';
        bytes.forEach(b => (binary += String.fromCharCode(b)));
        return `data:${contentType};base64,${btoa(binary)}`;
      }
    } catch { }
    return null;
  }

  async mapSongsWithDownloadStatus(songs: Song[]): Promise<Song[]> {
    if (!songs || songs.length === 0) return [];

    return await Promise.all(
      songs.map(async (song: Song) => {
        const isDownloaded = await this.isSongDownloaded(song.id);
        return { ...song, isDownloaded };
      })
    );
  }

  async isSongDownloaded(songId: number): Promise<boolean> {
    try {
      await Filesystem.stat({
        path: `${songId}.mp3`,
        directory: Directory.Data
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  async getLocalFileUrl(songId: number): Promise<string | null> {
    try {
      const result = await Filesystem.getUri({
        path: `${songId}.mp3`,
        directory: Directory.Data
      });
      return Capacitor.convertFileSrc(result.uri);
    } catch {
      return null;
    }
  }

  async getDownloadedSongsMetadata(): Promise<Song[]> {
    try {
      const dirResult = await Filesystem.readdir({ path: '', directory: Directory.Data });
      const files = dirResult.files.map(f => (typeof f === 'string' ? f : (f as any).name));
      const mp3Files = files.filter(f => f.endsWith('.mp3'));

      const songs: Song[] = [];
      for (const filename of mp3Files) {
        const id = filename.replace('.mp3', '');
        try {
          const meta = await Filesystem.readFile({
            path: `${id}.json`,
            directory: Directory.Data,
            encoding: Encoding.UTF8
          });
          const song: Song = JSON.parse(meta.data as string);
          // Le cover salvate con la nuova versione sono base64; quelle vecchie
          // sono HTTP URL che non caricano offline → le puliamo per mostrare il placeholder
          const cover = song.cover?.startsWith('data:') ? song.cover : undefined;
          songs.push({ ...song, isDownloaded: true, cover });
        } catch {
          // Sidecar JSON mancante: file audio presente ma metadati non disponibili
        }
      }
      return songs;
    } catch {
      return [];
    }
  }

  buildFiltersFromSongs(songs: Song[]): DisciplineModel[] {
    const map = new Map<number, DisciplineModel>();
    for (const song of songs) {
      if (!song.discipline) continue;
      if (!map.has(song.discipline.id)) {
        map.set(song.discipline.id, { ...song.discipline, dances: [], isSelected: false });
      }
      const disc = map.get(song.discipline.id);
      if (song.dance && !disc.dances.find(d => d.id === song.dance.id)) {
        disc.dances.push({ ...song.dance, isSelected: false });
      }
    }
    return Array.from(map.values());
  }

  private convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
